import { useEffect } from "react";
import "@/App.css";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import LandingPage from "@/pages/LandingPage";
import ArticlePage from "@/pages/ArticlePage";
import PatternStudyPage from "@/pages/PatternStudyPage";
import WhoWeArePage from "@/pages/WhoWeArePage";
import PrivacyPage from "@/pages/PrivacyPage";

function ScrollToHash() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return undefined;
    }
    let cancelled = false;
    const jump = () => {
      const el = document.querySelector(hash);
      if (!el || cancelled) return;
      const y = el.getBoundingClientRect().top + window.scrollY - 64;
      if (window.__lenis) {
        window.__lenis.scrollTo(y, { immediate: true });
      } else {
        window.scrollTo(0, y);
      }
    };
    // instant jumps at increasing delays: corrects for any late layout settle
    const timers = [250, 800, 1600].map((d) => setTimeout(jump, d));
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [pathname, hash]);
  return null;
}

function App() {
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return undefined;

    const lenis = new Lenis({ lerp: 0.08 });
    window.__lenis = lenis;
    let rafId;
    const loop = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = undefined;
    };
  }, []);

  return (
    <div className="bg-[#050505] text-[#FAFAFA] min-h-screen">
      <div className="noise-overlay" aria-hidden="true" />
      <ErrorBoundary>
        <BrowserRouter>
          <ScrollToHash />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/insights/:slug" element={<ArticlePage />} />
            <Route path="/patterns/:patternSlug" element={<PatternStudyPage />} />
            <Route path="/who-am-i" element={<WhoWeArePage />} />
            <Route path="/who-we-are" element={<Navigate to="/who-am-i" replace />} />
            <Route path="/privacy" element={<PrivacyPage />} />
          </Routes>
        </BrowserRouter>
      </ErrorBoundary>
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
}

export default App;
